import React from "react";
import {
  FiCalendar,
  FiEdit2,
  FiExternalLink,
  FiEye,
  FiImage,
  FiToggleLeft,
  FiToggleRight,
  FiTrash2,
} from "react-icons/fi";

import { toAbsolutePublicUrl } from "../../../../services/mediaLibraryServices";

import AdminButton from "../../_shared/ui/AdminButton";
import EventCategoryBadge from "./EventCategoryBadge";
import EventStatusBadge from "./EventStatusBadge";

import {
  formatEventDateParts,
  getEventPeriodLabel,
  truncateText,
} from "../utils/eventsAdminUtils";

export default function EventCard({
  item,
  actionId,
  onPreview,
  onEdit,
  onToggle,
  onAskDelete,
}) {
  const imageUrl = item?.photo_vignette
    ? toAbsolutePublicUrl(item.photo_vignette)
    : "";

  const dateParts = formatEventDateParts(item.start_date);
  const toggleLoading = actionId === `toggle-${item.id}`;

  return (
    <article className="group overflow-hidden rounded-[1.7rem] border border-slate-200 bg-slate-50 transition hover:-translate-y-0.5 hover:border-sky-200 hover:bg-white hover:shadow-xl hover:shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-500/30 dark:hover:bg-slate-900 dark:hover:shadow-black/20">
      <div className="grid min-h-full lg:grid-cols-[220px_1fr]">
        <div className="relative min-h-[220px] overflow-hidden bg-slate-200 dark:bg-slate-800">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={item.title}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full min-h-[220px] flex-col items-center justify-center gap-3 bg-gradient-to-br from-slate-100 to-slate-200 text-slate-400 dark:from-slate-800 dark:to-slate-900 dark:text-slate-500">
              <FiImage className="text-4xl" />
              <span className="text-xs font-bold uppercase tracking-[0.18em]">
                Sans image
              </span>
            </div>
          )}

          <div className="absolute left-3 top-3 overflow-hidden rounded-[1.25rem] bg-white text-center shadow-lg dark:bg-slate-950">
            <div className="bg-sky-600 px-4 py-1 text-xs font-bold uppercase text-white">
              {dateParts.month}
            </div>
            <div className="px-4 py-2">
              <p className="text-2xl font-bold leading-none text-slate-950 dark:text-white">
                {dateParts.day}
              </p>
              <p className="mt-1 text-xs font-bold text-slate-400">
                {item.all_day ? dateParts.year : dateParts.time}
              </p>
            </div>
          </div>
        </div>

        <div className="flex min-w-0 flex-col p-4 sm:p-5">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <EventCategoryBadge label={item.categorie} />
              <EventStatusBadge active={!!item.is_active} />

              {item.external_link ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-bold text-violet-700 dark:border-violet-500/20 dark:bg-violet-500/10 dark:text-violet-300">
                  <FiExternalLink />
                  Lien
                </span>
              ) : null}
            </div>

            <h3 className="mt-3 line-clamp-2 text-lg font-bold leading-snug text-slate-950 dark:text-white">
              {item.title || "Sans titre"}
            </h3>

            {item.subtitle ? (
              <p className="mt-1 line-clamp-1 text-sm font-bold text-sky-700 dark:text-sky-300">
                {item.subtitle}
              </p>
            ) : null}

            <div className="mt-3 flex items-center gap-2 text-sm font-bold text-slate-500 dark:text-slate-400">
              <FiCalendar />
              <span className="line-clamp-1">{getEventPeriodLabel(item)}</span>
            </div>

            <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {truncateText(item.description, 210) ||
                "Aucune description renseignée."}
            </p>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <AdminButton
              icon={FiEye}
              variant="secondary"
              size="sm"
              onClick={() => onPreview(item.id)}
            >
              Voir
            </AdminButton>

            <AdminButton
              icon={FiEdit2}
              variant="soft"
              size="sm"
              onClick={() => onEdit(item.id)}
            >
              Modifier
            </AdminButton>

            <AdminButton
              icon={item.is_active ? FiToggleLeft : FiToggleRight}
              variant={item.is_active ? "secondary" : "primary"}
              size="sm"
              loading={toggleLoading}
              onClick={() => onToggle(item.id)}
            >
              {item.is_active ? "Masquer" : "Publier"}
            </AdminButton>

            <AdminButton
              icon={FiTrash2}
              variant="dangerSoft"
              size="sm"
              onClick={() => onAskDelete(item.id)}
            >
              Supprimer
            </AdminButton>
          </div>
        </div>
      </div>
    </article>
  );
}
